import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CreateSignalDocument,
	type CreateSignalInput,
	DeleteSignalDocument,
	GetSignalCandidatesDocument,
	GetSignalSourcesDocument,
	GetSignalStatusesDocument,
	GetSignalsDocument,
	type SignalCandidatesInput,
	SyncSignalStatusDocument,
	UpdateSignalDocument,
	type UpdateSignalInput,
} from "@/common/artemis-sdk/graphql";
import type {
	Signal,
	SignalCandidate,
	SignalSourceInfo,
	SignalStatus,
} from "../model";
import { useGqlClient } from "../use-gql-client";

/**
 * How often the project page re-reads signal statuses while it is visible. TanStack Query pauses
 * interval refetches in a background tab, so a hidden page never polls the source.
 */
const STATUS_REFRESH_MS = 60_000;

/** A project's signal definitions. Never calls the signal sources. */
export function useSignals(projectId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["signals", projectId],
		queryFn: async () => {
			const data = await gqlFetch(GetSignalsDocument, { projectId });
			return data.signals as Signal[];
		},
		enabled: !!projectId,
	});
}

/**
 * The live status of each of a project's signals, keyed by signal id. Read from the sources
 * (cached ~30s server-side), separately from the definitions so a slow source never blocks the
 * rows from rendering.
 */
export function useSignalStatuses(projectId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["signalStatuses", projectId],
		queryFn: async () => {
			const data = await gqlFetch(GetSignalStatusesDocument, { projectId });
			return Object.fromEntries(
				data.signals.map((s) => [s.id, s.status]),
			) as Record<string, SignalStatus>;
		},
		enabled: !!projectId,
		refetchInterval: STATUS_REFRESH_MS,
	});
}

/** The signal sources and whether this workspace can use each one. */
export function useSignalSources(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["signalSources", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetSignalSourcesDocument, { workspaceId });
			return data.signalSources as SignalSourceInfo[];
		},
		enabled: !!workspaceId,
	});
}

/** What could be added as signals — for CloudWatch, the alarms in an environment's region. */
export function useSignalCandidates(input: SignalCandidatesInput | undefined) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["signalCandidates", input],
		queryFn: async () => {
			if (!input) return [];
			const data = await gqlFetch(GetSignalCandidatesDocument, { input });
			return data.signalCandidates as SignalCandidate[];
		},
		enabled: !!input?.environmentId,
	});
}

function useInvalidateSignals() {
	const queryClient = useQueryClient();
	return (projectId: string) => {
		queryClient.invalidateQueries({ queryKey: ["signals", projectId] });
		queryClient.invalidateQueries({ queryKey: ["signalStatuses", projectId] });
	};
}

/** Creates a signal. Invalidates the project's signals on success. */
export function useCreateSignal() {
	const gqlFetch = useGqlClient();
	const invalidate = useInvalidateSignals();
	return useMutation({
		mutationFn: async (input: CreateSignalInput) => {
			const data = await gqlFetch(CreateSignalDocument, { input });
			return data.createSignal.signal;
		},
		onSuccess: (_signal, input) => invalidate(input.projectId),
	});
}

/**
 * Re-reads one signal's status from its source now, bypassing the server cache, and drops the
 * result into the project's status map so every view of it updates at once.
 */
export function useSyncSignalStatus(projectId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (signalId: string) => {
			const data = await gqlFetch(SyncSignalStatusDocument, { signalId });
			return data.syncSignalStatus as SignalStatus;
		},
		onSuccess: (status, signalId) =>
			queryClient.setQueryData<Record<string, SignalStatus>>(
				["signalStatuses", projectId],
				(prev) => ({ ...(prev ?? {}), [signalId]: status }),
			),
	});
}

/** Renames a signal or changes its description. */
export function useUpdateSignal(projectId: string) {
	const gqlFetch = useGqlClient();
	const invalidate = useInvalidateSignals();
	return useMutation({
		mutationFn: async (variables: {
			signalId: string;
			input: UpdateSignalInput;
		}) => {
			const data = await gqlFetch(UpdateSignalDocument, variables);
			return data.updateSignal.signal;
		},
		onSuccess: () => invalidate(projectId),
	});
}

/** Deletes a signal. The alarm itself is untouched. */
export function useDeleteSignal(projectId: string) {
	const gqlFetch = useGqlClient();
	const invalidate = useInvalidateSignals();
	return useMutation({
		mutationFn: async (signalId: string) => {
			const data = await gqlFetch(DeleteSignalDocument, { signalId });
			return data.deleteSignal.success;
		},
		onSuccess: () => invalidate(projectId),
	});
}
